<script lang="ts" setup>
const { createEvent } = useAnalytics()
const { createToast } = useToasts()
const { t } = useI18n()
const guards = useGuards()
const localePath = useLocalePath()
const route = useRoute()
const router = useRouter()
const teamsStore = useTeamsStore()
const userStore = useUserStore()

const processingInvite = ref(false)

guards.visitorOnly()

const status = computed(() => {
  if (processingInvite.value) {
    return 'processingInvite'
  }
  else if (route.query.invitationToken) {
    return 'join'
  }
  else {
    return 'default'
  }
})

onMounted(async () => {
  createEvent({ name: '[Navigation] Visit Login' })

  if (userStore.user && route.query.invitationToken) {
    await processInvite(`${route.query.invitationToken}`)

    router.push(localePath('index'))
  }
})

async function processInvite(invitationToken: string) {
  processingInvite.value = true

  try {
    const team = await userStore.processUserTeamInvitation({ invitationToken })

    // Switch to accepted team
    await teamsStore.switchTeam({ teamId: team.id })

    createEvent({ name: '[Teams] Process Team Invitation' })
    createToast({ message: 'teamInvitationAccepted' })
  }
  catch (error) {
    console.error(error)
  }

  processingInvite.value = false
}
</script>

<template>
  <UContainer class="flex flex-col items-center py-20">
    <BasePower class="mb-4" />

    <BaseHeading
      class="mb-8"
      :description="status !== 'processingInvite' ? t('description') : ''"
      tag="h1"
      :title="t(`title.${status}`)"
    />

    <AuthBox
      v-if="!processingInvite"
      current-view="login"
      size="lg"
    />

    <BaseSpinner
      v-else
      class="size-10 md:size-12"
    />

    <div class="hidden sm:flex sm:flex-col">
      <UDivider class="mx-auto my-8 max-w-md" />

      <ProductTrophies />
    </div>
  </UContainer>
</template>

<i18n lang="json">
{
  "en": {
    "description": "Create your account or login in one single click.",
    "title": {
      "default": "Welcome to The Companies API",
      "join": "Join your new team in one single click",
      "processingInvite": "Accepting the team invitation…"
    }
  },
  "es": {
    "description": "Crea tu cuenta o inicia sesión en un solo clic.",
    "title": {
      "default": "Bienvenido a The Companies API",
      "join": "Únete a tu nueva equipo en un solo clic",
      "processingInvite": "Aceptando la invitación de equipo…"
    }
  },
  "fr": {
    "description": "Créez votre compte ou connectez-vous en un seul clic.",
    "title": {
      "default": "Bienvenue sur The Companies API",
      "join": "Rejoignez votre nouvelle équipe en un seul clic",
      "processingInvite": "Acceptation de l'invitation de l'équipe…"
    }
  }
}
</i18n>
